import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  OnboardingApiError,
  isSlimView,
  onboardingPublicAPI,
  type SlimLeadView,
  type DataSlot,
  type LeadAnswers,
  type LeadProfile,
  type Ownership,
  type PublicFileView,
  type PublicLeadResponse,
  type PublicLeadView,
  type PublicSlotInfo,
  type YesNoUnknown,
} from "../OnboardingAPI";
import { sessionFlag } from "./calm";
import { readHandle, storeHandle } from "./portalHandle";
import { firstNameOf } from "./firstName";
import type { UploadItem } from "./SlotUploader";
import {
  LOGO_SLOT,
  buildSavePayload,
  fieldProblem,
  fieldProblems,
  FIELD_MAX,
  checkFileBeforeUpload,
  filesOfSlot,
  formatBytes,
  missingRequiredSlots,
  payloadKey,
  splitContactName,
  submitBlockers,
  type FormValues,
} from "./onboardingModel";

/** What the page shows instead of the form when the link itself cannot be used. */
/** Everything this page keeps in the browser for a link, except the "show" preference. */
export function purgeStorage(token: string) {
  [window.sessionStorage, window.localStorage].forEach((storage) => {
    try {
      Object.keys(storage)
        .filter((key) => key.startsWith("onboarding.") && key.endsWith(`.${token}`) && key !== `onboarding.opened.${token}` && key !== `onboarding.portal.${token}`)
        .forEach((key) => storage.removeItem(key));
    } catch {
      // no storage
    }
  });
}

export type PageProblem = "invalid_token" | "token_expired" | "rate_limited" | "error";
export type SaveStatus = "idle" | "saving" | "saved" | "error" | "locked";

export const SLOT_TEXT_KEYS = new Set<string>([
  "buildings",
  "assets",
  "spaces",
  "floorplans",
  "assetDetails",
  "maintenance",
  "responsibilities",
  "catalogs",
  "escapePlans",
  "other",
]);

/** What the server's `missing_required.missing` entries mean on this page. */
const MISSING_TEXT_KEYS: Record<string, string> = {
  files: "onboardingPage.submit.missingData",
  "profile.companyName": "onboardingPage.submit.missingCompanyName",
  "profile.contactName": "onboardingPage.submit.missingContactName",
  "profile.contactFirstName": "onboardingPage.submit.missingContactFirstName",
  "profile.contactLastName": "onboardingPage.submit.missingContactLastName",
  "profile.contactEmail": "onboardingPage.submit.missingContactEmail",
};

/** The slim view is read again this often after the submit, this many times at most (2 minutes). */
export const ACCESS_POLL_MS = 5000;
export const ACCESS_POLL_MAX = 24;

/** A 429 with a `Retry-After` up to this many seconds is retried automatically, this many times. */
const AUTO_RETRY_MAX = 2;
const AUTO_RETRY_MAX_WAIT_S = 60;

export const OWNERSHIPS: Ownership[] = ["owner", "operator", "both"];
export const CHOICES: YesNoUnknown[] = ["yes", "no", "unknown"];

const toFormValues = (view: PublicLeadView): FormValues => {
  const profile = { ...view.profile };
  // an older lead has only the contact name: its words fill the two fields (the person sees and can correct them)
  if (profile.contactFirstName === undefined && profile.contactLastName === undefined) {
    const { first, last } = splitContactName(profile.contactName);
    profile.contactFirstName = first;
    profile.contactLastName = last;
  }
  return { profile, answers: { ...view.answers } };
};

const problemFor = (error: unknown): PageProblem => {
  if (error instanceof OnboardingApiError) {
    if (error.code === "invalid_token") return "invalid_token";
    if (error.code === "token_expired") return "token_expired";
    if (error.code === "rate_limited") return "rate_limited";
  }
  return "error";
};

let uploadCounter = 0;
const nextUploadId = () => {
  uploadCounter += 1;
  return `upload-${uploadCounter}`;
};

/**
 * Everything the public onboarding page does besides drawing: loading the lead, the autosave of the
 * profile and answers, the upload queue, the logo, the submit, the rules of the fields. The steps
 * (`OnboardingPage.tsx`, `steps/`) only show it. Contract: tri-server `docs/onboarding-leads.md`.
 */
export function useOnboardingController() {
  const { token = "" } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en") ? "en-GB" : "de-DE";

  const [view, setView] = useState<PublicLeadView | null>(null);
  /** Once the lead has left "open" the page keeps only this: status, first name, welcome. Nothing the person typed. */
  const [slim, setSlim] = useState<SlimLeadView | null>(null);
  /** The access is not there after the time the page waits for it: a calm sentence instead of the spinner. */
  const [accessSlow, setAccessSlow] = useState(false);
  const [problem, setProblem] = useState<PageProblem | null>(null);
  const [values, setValues] = useState<FormValues | null>(null);
  const [greetingName, setGreetingName] = useState("");
  /** The contact name the invitation carried, from the first load: the greeting takes the first name out of it. */
  const [greetingContact, setGreetingContact] = useState("");
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [slotNotices, setSlotNotices] = useState<Partial<Record<DataSlot, string>>>({});
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  // fields the person has left once: a rule is explained then, not while still typing
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Refs hold what asynchronous work must see at the moment it runs, not at the render that started it.
  const viewRef = useRef<PublicLeadView | null>(null);
  const valuesRef = useRef<FormValues | null>(null);
  const uploadsRef = useRef<UploadItem[]>([]);
  const savedKeyRef = useRef("");
  /** The number the code check is about, as the form held it when it was last taken from the server or sent a code. */
  const phoneBaseRef = useRef("");
  const inFlightRef = useRef<Promise<boolean> | null>(null);
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const abortRef = useRef<(() => void) | null>(null);
  const logoPreviewRef = useRef<string | null>(null);
  /** The preview that was showing when a replacement started; it comes back if the replacement fails. */
  const previousPreviewRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const lastSaveErrorRef = useRef<OnboardingApiError | null>(null);
  /** The server refused a value (`invalid_request` with a `field`): shown at that field for as long as the value is the same. */
  const [rejected, setRejected] = useState<Record<string, string>>({});
  viewRef.current = view;
  valuesRef.current = values;
  uploadsRef.current = uploads;

  const applyView = useCallback((next: PublicLeadView) => {
    setView(next);
    const form = toFormValues(next);
    setValues(form);
    savedKeyRef.current = payloadKey(form);
    phoneBaseRef.current = (form.profile.contactPhone ?? "").trim();
  }, []);

  /**
   * The lead is no longer open: from here on the page shows the status and nothing else. Everything the
   * person gave - the form, the file list and names, previews, the step, the "left empty" marks - is
   * dropped from the state and from the storages of this page, so none of it stays in the DOM or the browser.
   */
  const enterClosed = useCallback(
    (response: PublicLeadResponse) => {
      const full = response as Partial<PublicLeadView>;
      const typedName = valuesRef.current?.profile.contactFirstName || full.profile?.contactFirstName || valuesRef.current?.profile.contactName || full.profile?.contactName;
      const closed: SlimLeadView = {
        customerStatus: (response.customerStatus === "open" ? "received" : response.customerStatus) as SlimLeadView["customerStatus"],
        expiresAt: response.expiresAt,
        canEdit: false,
        firstName: (response as SlimLeadView).firstName ?? firstNameOf(typedName),
        welcome: response.welcome,
        // only ever `true` is kept: the page says something about the access mail only when it went out
        ...((response as SlimLeadView).accessSent === true ? { accessSent: true } : {}),
        ...((response as SlimLeadView).accountReady === true ? { accountReady: true } : {}),
      };
      // stop what is still running
      queueRef.current = Promise.resolve();
      abortRef.current?.();
      if (logoPreviewRef.current) URL.revokeObjectURL(logoPreviewRef.current);
      if (previousPreviewRef.current) URL.revokeObjectURL(previousPreviewRef.current);
      logoPreviewRef.current = null;
      previousPreviewRef.current = null;
      viewRef.current = null;
      valuesRef.current = null;
      uploadsRef.current = [];
      savedKeyRef.current = "";
      setView(null);
      setValues(null);
      setUploads([]);
      setSlotNotices({});
      setLogoPreview(null);
      setTouched({});
      setGreetingName("");
      setGreetingContact("");
      setSubmitError(null);
      purgeStorage(token);
      setSlim(closed);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token],
  );

  const load = useCallback(async () => {
    setProblem(null);
    try {
      const loaded = await onboardingPublicAPI.getLead(token);
      if (isSlimView(loaded)) enterClosed(loaded);
      else {
        setGreetingName(loaded.profile.companyName ?? "");
        setGreetingContact((current) => current || loaded.profile.contactFirstName || loaded.profile.contactName || "");
        applyView(loaded);
      }
      // the page was opened: counted once per tab session, and never when the link was unusable
      const opened = sessionFlag(`onboarding.opened.${token}`);
      if (!opened.seen()) {
        opened.mark();
        void Promise.resolve(onboardingPublicAPI.recordOpen(token)).catch(() => undefined);
      }
    } catch (error) {
      setProblem(problemFor(error));
    }
  }, [token, applyView, enterClosed]);

  useEffect(() => {
    void load();
  }, [load]);

  // When the page goes away: stop the running upload, let the waiting ones not start, release the preview.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      queueRef.current = Promise.resolve();
      abortRef.current?.();
      if (logoPreviewRef.current) URL.revokeObjectURL(logoPreviewRef.current);
    };
  }, []);

  /** A refused call that says the link or the lead changed under us. */
  const handleActionError = useCallback(
    (error: unknown) => {
      if (!(error instanceof OnboardingApiError)) return;
      if (error.code === "token_expired") setProblem("token_expired");
      else if (error.code === "invalid_token") setProblem("invalid_token");
      else if (error.code === "locked") void load();
    },
    [load],
  );

  /**
   * The look at a stored file (what the server holds). A refusal that says the lead changed (`locked`, an expired link)
   * is handled like any other action's, and still reaches the dialog, which closes.
   */
  const preview = useMemo(
    () => ({
      load: async (fileId: string) => {
        try {
          return await onboardingPublicAPI.previewFile(token, fileId);
        } catch (error) {
          handleActionError(error);
          throw error;
        }
      },
      content: async (fileId: string) => {
        try {
          return await onboardingPublicAPI.fileContent(token, fileId);
        } catch (error) {
          handleActionError(error);
          throw error;
        }
      },
    }),
    [token, handleActionError],
  );

  // ---- autosave -----------------------------------------------------------

  const doSave = useCallback(
    async (current: FormValues, key: string): Promise<boolean> => {
      setSaveStatus("saving");
      try {
        const next = await onboardingPublicAPI.saveProfile(token, buildSavePayload(current));
        savedKeyRef.current = key;
        lastSaveErrorRef.current = null;
        // Only the state the server owns is taken over. Replacing the form (or the file list) with
        // the response would drop what was typed or uploaded while the request was on its way.
        if (isSlimView(next)) {
          // locked from another tab: nothing of the form stays
          enterClosed(next);
          return true;
        }
        setView((previous) =>
          previous
            ? { ...previous, customerStatus: next.customerStatus, canEdit: next.canEdit, expiresAt: next.expiresAt }
            : previous,
        );
        if (valuesRef.current && payloadKey(valuesRef.current) === key) setSaveStatus("saved");
        return true;
      } catch (error) {
        lastSaveErrorRef.current = error instanceof OnboardingApiError ? error : null;
        if (error instanceof OnboardingApiError && error.code === "invalid_request" && error.field) {
          const field = error.field;
          const value = (current.profile as unknown as Record<string, string | undefined>)[field] ?? (current.answers as unknown as Record<string, string | undefined>)[field];
          if (value !== undefined) setRejected((previous) => ({ ...previous, [field]: value }));
        }
        setSaveStatus("error");
        handleActionError(error);
        return false;
      }
    },
    [token, handleActionError, enterClosed],
  );

  /** Saves until the server holds the current form. Resolves false if a save failed. */
  const flush = useCallback(async (): Promise<boolean> => {
    for (;;) {
      if (inFlightRef.current) {
        await inFlightRef.current;
        continue;
      }
      const current = valuesRef.current;
      if (!current || !viewRef.current?.canEdit) return true;
      const key = payloadKey(current);
      if (key === savedKeyRef.current) return true;
      const run = doSave(current, key);
      inFlightRef.current = run;
      const ok = await run;
      if (inFlightRef.current === run) inFlightRef.current = null;
      if (!ok) return false;
    }
  }, [doSave]);

  useEffect(() => {
    if (!values || !view?.canEdit) return;
    if (payloadKey(values) === savedKeyRef.current) return;
    setSaveStatus("saving");
    const timer = setTimeout(() => {
      void flush();
    }, 800);
    return () => clearTimeout(timer);
  }, [values, view?.canEdit, flush]);

  // Closing the tab with something the server does not hold yet (an edit still waiting for its save, a
  // failed save, a field held back for breaking a rule, an upload on its way) asks first.
  const unsavedChanges =
    !!values &&
    !!view?.canEdit &&
    (payloadKey(values) !== savedKeyRef.current ||
      saveStatus === "error" ||
      Object.keys(fieldProblems(values)).length > 0 ||
      uploads.some((item) => item.status === "waiting" || item.status === "uploading"));
  useEffect(() => {
    if (!unsavedChanges) return;
    const guard = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // legacy browsers need a returnValue to show the prompt
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [unsavedChanges]);

  const updateProfile = (field: keyof LeadProfile, value: string) => {
    setValues((previous) => (previous ? { ...previous, profile: { ...previous.profile, [field]: value } } : previous));
    // another number: the server lets the confirmation lapse when it saves it, and the page says so at once
    if (field === "contactPhone" && value.trim() !== phoneBaseRef.current) {
      setView((previous) => (previous?.phone && (previous.phone.verified || previous.phone.codeSent) ? { ...previous, phone: { ...previous.phone, verified: false, codeSent: false } } : previous));
    }
  };

  // ---- the mobile number: a code by SMS ------------------------------------------------------------

  /**
   * Sends the code (to the stored number, or - with `newPhone` - to a new one, which the server stores). The answer sets
   * when the next code may be sent; a 429 does the same from its own `nextSendAt` / `Retry-After`. Errors go on to the step.
   */
  const sendPhoneCode = async (newPhone?: string) => {
    if (newPhone === undefined && !(await flush())) throw lastSaveErrorRef.current ?? new OnboardingApiError(0, "network");
    try {
      const answer = await onboardingPublicAPI.sendPhoneCode(token, newPhone);
      const stated = typeof answer?.phone === "object" ? answer.phone : undefined;
      const masked = typeof answer?.phone === "string" ? answer.phone : stated?.number;
      const nextSendAt = answer?.nextSendAt ?? stated?.nextSendAt;
      setView((previous) => (previous ? { ...previous, phone: { number: masked ?? previous.phone?.number, verified: false, codeSent: true, nextSendAt } } : previous));
      if (newPhone !== undefined) {
        // the server has stored it: the form shows it, and the autosave must not send it again (the server would see a
        // different spelling of the number and let the confirmation lapse)
        const next = valuesRef.current ? { ...valuesRef.current, profile: { ...valuesRef.current.profile, contactPhone: newPhone } } : null;
        if (next) {
          valuesRef.current = next;
          setValues(next);
          savedKeyRef.current = payloadKey(next);
        }
        phoneBaseRef.current = newPhone.trim();
      }
      return { nextSendAt };
    } catch (error) {
      if (error instanceof OnboardingApiError && error.code === "rate_limited") {
        const at = error.nextSendAt ?? (error.retryAfterSeconds ? new Date(Date.now() + error.retryAfterSeconds * 1000).toISOString() : undefined);
        if (at) setView((previous) => (previous?.phone ? { ...previous, phone: { ...previous.phone, nextSendAt: at } } : previous));
      }
      handleActionError(error);
      throw error;
    }
  };

  const verifyPhoneCode = async (code: string) => {
    try {
      await onboardingPublicAPI.verifyPhoneCode(token, code);
      setView((previous) => (previous?.phone ? { ...previous, phone: { ...previous.phone, verified: true, codeSent: true } } : previous));
    } catch (error) {
      handleActionError(error);
      throw error;
    }
  };
  const updateAnswers = (field: keyof LeadAnswers, value: string) =>
    setValues((previous) => (previous ? { ...previous, answers: { ...previous.answers, [field]: value } } : previous));

  // ---- uploads ------------------------------------------------------------

  const errorText = useCallback(
    (code: string, slot: PublicSlotInfo, retryAfterSeconds?: number): string => {
      const current = viewRef.current;
      if (code === "rate_limited" && retryAfterSeconds) {
        return t("onboardingPage.errors.rate_limited_after", { seconds: retryAfterSeconds });
      }
      if (code === "invalid_file_type") {
        const list = slot.extensions.map((extension) => extension.replace(/^\./, "").toUpperCase()).join(", ");
        return t("onboardingPage.errors.invalid_file_type", { list });
      }
      if (code === "file_too_large") {
        // The logo has its own, smaller limit that the view does not carry: do not name a wrong one.
        if (!current) return t("onboardingPage.errors.file_too_large_nolimit");
        if (slot.slot === LOGO_SLOT) {
          // The logo's own limit, once the server names it; otherwise name none rather than a wrong one.
          return current.limits.maxLogoBytes
            ? t("onboardingPage.errors.file_too_large", { size: formatBytes(current.limits.maxLogoBytes, locale) })
            : t("onboardingPage.errors.file_too_large_nolimit");
        }
        return t("onboardingPage.errors.file_too_large", { size: formatBytes(current.limits.maxFileSizeBytes, locale) });
      }
      const key = `onboardingPage.errors.${code}`;
      return i18n.exists(key) ? t(key) : t("onboardingPage.errors.unknown");
    },
    [t, i18n, locale],
  );

  const patchUpload = (id: string, patch: Partial<UploadItem>) =>
    setUploads((previous) => previous.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  const dropUpload = (id: string) => setUploads((previous) => previous.filter((item) => item.id !== id));

  const runUpload = useCallback(
    async (id: string) => {
      const stillThere = () => mountedRef.current && uploadsRef.current.some((candidate) => candidate.id === id);
      // The page is gone: a file still waiting in the queue must not start.
      if (!mountedRef.current) return;
      const item = uploadsRef.current.find((candidate) => candidate.id === id);
      // Dismissed while it waited in the queue.
      if (!item || item.status === "error" || item.status === "done") return;

      for (let attempt = 0; ; attempt += 1) {
        patchUpload(id, { status: "uploading", progress: 0, retrying: false });
        const { promise, abort } = onboardingPublicAPI.uploadFile(token, item.slot, item.file, (fraction) =>
          patchUpload(id, { progress: fraction }),
        );
        abortRef.current = abort;
        try {
          const stored = await promise;
          // The server keeps one logo and replaces the old one itself: mirror that, never both.
          setView((previous) =>
            previous
              ? {
                  ...previous,
                  files: [...(item.slot === LOGO_SLOT ? previous.files.filter((f) => f.slot !== LOGO_SLOT) : previous.files), stored],
                }
              : previous,
          );
          if (item.slot === LOGO_SLOT && previousPreviewRef.current) {
            URL.revokeObjectURL(previousPreviewRef.current);
            previousPreviewRef.current = null;
          }
          // The item stays (as `done`): its row keeps its identity instead of being replaced by a new one.
          patchUpload(id, { status: "done", progress: 1, fileId: stored.id });
          return;
        } catch (error) {
          const limited = error instanceof OnboardingApiError && error.code === "rate_limited" ? error : null;
          const wait = limited?.retryAfterSeconds;
          // A 429 that says how long to wait is retried on its own - a short wait, a few times, and the
          // person can cancel the row. Without the header (an older backend) it stays a plain error with
          // a retry button.
          if (limited && wait && wait <= AUTO_RETRY_MAX_WAIT_S && attempt < AUTO_RETRY_MAX && stillThere()) {
            patchUpload(id, { status: "waiting", retrying: true, progress: 0, retryAfterSeconds: wait });
            const until = Date.now() + wait * 1000;
            while (Date.now() < until && stillThere()) await new Promise((resolve) => setTimeout(resolve, 200));
            if (!stillThere()) return;
            continue;
          }
          // "the content does not fit the name" is its own text: the allowed types would only mislead there
          const code = error instanceof OnboardingApiError ? (error.code === "invalid_file_type" && error.reason === "content" ? "invalid_file_content" : error.code) : "unknown";
          patchUpload(id, { status: "error", errorCode: code, retryAfterSeconds: limited?.retryAfterSeconds, retrying: false });
          if (item.slot === LOGO_SLOT) restoreLogoPreview();
          handleActionError(error);
          return;
        } finally {
          abortRef.current = null;
        }
      }
    },
    [token, handleActionError],
  );

  const enqueue = useCallback(
    (id: string) => {
      queueRef.current = queueRef.current.then(() => runUpload(id));
    },
    [runUpload],
  );

  const addFiles = useCallback(
    (slotInfo: PublicSlotInfo, picked: File[]) => {
      const current = viewRef.current;
      if (!current || !current.canEdit) return;
      const created: UploadItem[] = [];
      picked.forEach((file) => {
        const pending = [...uploadsRef.current, ...created].filter(
          (item) => item.status === "waiting" || item.status === "uploading",
        );
        const refusal = checkFileBeforeUpload(
          file,
          slotInfo,
          current,
          pending.filter((item) => item.slot === slotInfo.slot).length,
          pending.length,
        );
        created.push({
          id: nextUploadId(),
          slot: slotInfo.slot,
          file,
          status: refusal ? "error" : "waiting",
          progress: 0,
          errorCode: refusal ?? undefined,
        });
      });
      // The ref is updated here as well: a second drop in the same tick must count the first.
      uploadsRef.current = [...uploadsRef.current, ...created];
      setUploads(uploadsRef.current);
      created.filter((item) => item.status === "waiting").forEach((item) => enqueue(item.id));
    },
    [enqueue],
  );

  const retryUpload = (item: UploadItem) => {
    patchUpload(item.id, { status: "waiting", progress: 0, errorCode: undefined });
    uploadsRef.current = uploadsRef.current.map((candidate) =>
      candidate.id === item.id ? { ...candidate, status: "waiting", errorCode: undefined } : candidate,
    );
    enqueue(item.id);
  };

  const removeStored = async (file: PublicFileView) => {
    setSlotNotices((previous) => ({ ...previous, [file.slot]: undefined }));
    try {
      await onboardingPublicAPI.deleteFile(token, file.id);
      setView((previous) => (previous ? { ...previous, files: previous.files.filter((f) => f.id !== file.id) } : previous));
      setUploads((previous) => previous.filter((item) => item.fileId !== file.id));
    } catch (error) {
      if (error instanceof OnboardingApiError && error.code === "not_found") {
        // already gone on the server: the list should say so too
        setView((previous) => (previous ? { ...previous, files: previous.files.filter((f) => f.id !== file.id) } : previous));
        setUploads((previous) => previous.filter((item) => item.fileId !== file.id));
        return;
      }
      handleActionError(error);
      const code = error instanceof OnboardingApiError ? error.code : "unknown";
      const slotInfo = viewRef.current?.slots.find((slot) => slot.slot === file.slot);
      if (slotInfo) setSlotNotices((previous) => ({ ...previous, [file.slot]: errorText(code, slotInfo) }));
    }
  };

  const setPreview = (url: string | null) => {
    if (logoPreviewRef.current) URL.revokeObjectURL(logoPreviewRef.current);
    logoPreviewRef.current = url;
    setLogoPreview(url);
  };

  /** A failed replacement: drop the new image's preview and show the old logo again. */
  function restoreLogoPreview() {
    if (logoPreviewRef.current) URL.revokeObjectURL(logoPreviewRef.current);
    logoPreviewRef.current = previousPreviewRef.current;
    previousPreviewRef.current = null;
    setLogoPreview(logoPreviewRef.current);
  }

  const pickLogo = async (file: File | undefined, slotInfo: PublicSlotInfo) => {
    const current = viewRef.current;
    if (!file || !current?.canEdit) return;
    // The logo has its own, smaller limit once the server names it; until then the server decides (413).
    const limits = {
      ...current.limits,
      maxFileSizeBytes: Math.min(current.limits.maxFileSizeBytes, current.limits.maxLogoBytes ?? current.limits.maxFileSizeBytes),
    };
    const refusal = checkFileBeforeUpload(file, { ...slotInfo, maxFiles: undefined }, { ...current, files: [], limits }, 0, 0);
    if (refusal) {
      setSlotNotices((previous) => ({ ...previous, [LOGO_SLOT]: errorText(refusal, slotInfo) }));
      return;
    }
    setSlotNotices((previous) => ({ ...previous, [LOGO_SLOT]: undefined }));
    // Nothing is deleted beforehand: the server replaces the old logo with the new one in one step, so
    // a failed upload leaves the old logo where it was.
    if (previousPreviewRef.current) URL.revokeObjectURL(previousPreviewRef.current);
    previousPreviewRef.current = logoPreviewRef.current;
    logoPreviewRef.current = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
    setLogoPreview(logoPreviewRef.current);
    const item: UploadItem = { id: nextUploadId(), slot: LOGO_SLOT, file, status: "waiting", progress: 0 };
    uploadsRef.current = [...uploadsRef.current.filter((candidate) => candidate.slot !== LOGO_SLOT), item];
    setUploads(uploadsRef.current);
    enqueue(item.id);
  };

  // After a reload the logo is only a file on the server: ask for it, or show the name if there is no route.
  const storedLogoId = view ? filesOfSlot(view.files, LOGO_SLOT)[0]?.id : undefined;
  useEffect(() => {
    if (!storedLogoId || logoPreviewRef.current) return;
    let cancelled = false;
    void onboardingPublicAPI.getLogo(token).then((blob) => {
      if (cancelled || !blob || logoPreviewRef.current) return;
      logoPreviewRef.current = URL.createObjectURL(blob);
      setLogoPreview(logoPreviewRef.current);
    });
    return () => {
      cancelled = true;
    };
  }, [storedLogoId, token]);

  const removeLogo = async (file: PublicFileView) => {
    await removeStored(file);
    setPreview(null);
  };

  // After the submit the access is made in the background: the slim view is read again every 5 s (only in a tab that is
  // seen) until the access mail has gone out - and the account is ready, where this tab holds the handle for the direct
  // sign-in - for 2 minutes at most.
  const accessDone = !!slim && slim.accessSent === true && (!readHandle(token) || slim.accountReady === true);
  useEffect(() => {
    if (!slim || accessDone) return;
    let polls = 0;
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      polls += 1;
      void Promise.resolve(onboardingPublicAPI.getLead(token))
        .then((next) => {
          if (!isSlimView(next)) return;
          setSlim((previous) => (previous ? { ...previous, ...next, firstName: next.firstName ?? previous.firstName } : previous));
        })
        .catch(() => undefined);
      if (polls >= ACCESS_POLL_MAX) {
        window.clearInterval(timer);
        setAccessSlow(true);
      }
    }, ACCESS_POLL_MS);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!slim, accessDone, token]);

  // ---- submit -------------------------------------------------------------

  /** A refused or failed call in the page's own words; the server names what is missing on a 422. */
  const describeError = (error: unknown): string => {
    const known = error instanceof OnboardingApiError ? error : null;
    const code = known?.code ?? "unknown";
    if (known && code === "missing_required" && known.missing?.length) {
      const names = known.missing
        .map((entry) => MISSING_TEXT_KEYS[entry])
        .filter(Boolean)
        .map((textKey) => t(textKey));
      if (names.length) return `${t("onboardingPage.submit.missingPrefix")} ${names.join(", ")}.`;
    }
    if (known && code === "rate_limited" && known.retryAfterSeconds) {
      return t("onboardingPage.errors.rate_limited_after", { seconds: known.retryAfterSeconds });
    }
    const key = `onboardingPage.errors.${code}`;
    return i18n.exists(key) ? t(key) : t("onboardingPage.errors.unknown");
  };

  const submit = async (): Promise<"sent" | "phone_not_verified" | "failed"> => {
    setSubmitting(true);
    setSubmitError(null);
    const saved = await flush();
    if (!saved) {
      // Name what really went wrong with the save - a refused field is not a lost connection.
      setSubmitting(false);
      setSubmitError(describeError(lastSaveErrorRef.current));
      return "failed";
    }
    try {
      const next = await onboardingPublicAPI.submit(token);
      enterClosed(next);
      // the handle of the direct sign-in: kept for this tab only
      const handle = (next as { portalAccess?: { handle?: unknown } }).portalAccess?.handle;
      if (typeof handle === "string" && handle) storeHandle(token, handle);
      // The page scrolls inside #root (index.css gives html/body a fixed height), not the window.
      document.getElementById("root")?.scrollTo?.({ top: 0, behavior: "smooth" });
      window.scrollTo?.({ top: 0, behavior: "smooth" });
      return "sent";
    } catch (error) {
      if (error instanceof OnboardingApiError && error.code === "phone_not_verified") {
        // the server does not count the number as confirmed: the page goes back to the step that does it
        setView((previous) => (previous?.phone ? { ...previous, phone: { ...previous.phone, verified: false } } : previous));
        return "phone_not_verified";
      }
      handleActionError(error);
      const text = describeError(error);
      setSubmitError(text);
      return "failed";
    } finally {
      setSubmitting(false);
    }
  };

  // ---- derived ------------------------------------------------------------

  const uploadsRunning = uploads.some((item) => item.status === "waiting" || item.status === "uploading");
  const blockers = useMemo(
    () => (view && values ? submitBlockers(values, view, uploadsRunning) : []),
    [view, values, uploadsRunning],
  );
  /** A step was entered: told to the server once per step and session, and not faster than every 2 s. */
  const lastProgressRef = useRef(0);
  const reportStep = useCallback(
    (slug: string) => {
      const key = `onboarding.reported.${token}`;
      let reported: string[] = [];
      try {
        reported = JSON.parse(window.sessionStorage.getItem(key) ?? "[]");
      } catch {
        reported = [];
      }
      if (reported.includes(slug)) return;
      try {
        window.sessionStorage.setItem(key, JSON.stringify([...reported, slug]));
      } catch {
        // ignore
      }
      const wait = Math.max(0, 2000 - (Date.now() - lastProgressRef.current));
      lastProgressRef.current = Date.now() + wait;
      window.setTimeout(() => {
        if (mountedRef.current) void Promise.resolve(onboardingPublicAPI.recordProgress(token, slug)).catch(() => undefined);
      }, wait);
    },
    [token],
  );

  const missingSlots = useMemo(() => (view ? missingRequiredSlots(view) : []), [view]);

  /** The message under a field that breaks a server rule (a too-long text at once, a format after leaving the field). */
  const problemText = (field: string, raw: string | undefined): string | null => {
    const found = fieldProblem(field, raw);
    // a value the server refused stays marked until it is changed (our own sentence, never the server's)
    if (!found && rejected[field] !== undefined && rejected[field] === (raw ?? "")) return t("onboardingPage.errors.field_invalid");
    if (!found) return null;
    if (found === "tooLong") return t("onboardingPage.fieldTooLong", { max: FIELD_MAX[field] });
    if (!touched[field]) return null;
    return t("onboardingPage.company.websiteInvalid");
  };
  const touch = (field: string) => setTouched((previous) => (previous[field] ? previous : { ...previous, [field]: true }));

  return {
    token, t, i18n, locale,
    view, slim, accessSlow, problem, values, greetingName, greetingContact, reportStep, saveStatus, uploads, slotNotices, logoPreview, touched,
    submitting, submitError, setSubmitError, setSlotNotices,
    load, flush, updateProfile, updateAnswers, touch, problemText, errorText, describeError,
    preview, addFiles, retryUpload, removeStored, dropUpload, pickLogo, removeLogo, submit, sendPhoneCode, verifyPhoneCode,
    uploadsRunning, blockers, missingSlots, unsavedChanges,
  };
}

export type OnboardingController = ReturnType<typeof useOnboardingController>;
